import { Comments } from 'components/Comments';
import { useSave } from 'hooks/useSave';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import projectSlice from 'redux/reducers/project';
import type { Comment, Project } from 'types';
import { useResolvedProject, useResolvedWorkSteps } from 'hooks/useResolved';
import { createResolvedComments } from 'pages/EditProcedure/components/Worksteps/Review/Resolved';
import { checkIfNewReviewerCommentsFlow } from 'utils/project';
import { useAnyUnresolvedComments } from 'hooks/useUnresolvedComments';
import { ProjectCard } from '../ProjectCard/ProjectCard';
import { StepRevision } from '../StepRevision';
import { ResolveAction } from './components/ResolveAction';
import { ReviewCommentList } from './components/ReviewCommentList/ReviewCommentList';
import { CommentsHeaderContainer, CommentsHeaderTitle } from './elements';

export type EnhancedReviewCommentsProps = {
  project: Project;
  comments: Comment[];
  user: string;
  viewOnly: boolean;
};

export const EnhancedReviewComments = ({
  project,
  comments,
  user,
  viewOnly,
}: EnhancedReviewCommentsProps) => {
  const dispatch = useDispatch();
  const { saveReviewComment, saveHandler } = useSave();
  const isProjectResolved = useResolvedProject();
  const isWorkStepsResolved = useResolvedWorkSteps();
  const isCommentsUnresolved = useAnyUnresolvedComments();
  const isInitialMount = React.useRef<boolean>(true);

  const shouldRenderNewCommentFlow = useMemo(
    () => checkIfNewReviewerCommentsFlow(project),
    [project]
  );

  const shouldHighlightBorder = useMemo(
    () => !isProjectResolved || !isWorkStepsResolved || isCommentsUnresolved,
    [isProjectResolved, isWorkStepsResolved, isCommentsUnresolved]
  );

  const oldCommentList = useMemo(
    () => comments.concat(createResolvedComments(project.resolved) || []),
    [comments, project.resolved]
  );
  const userRole =
    project.status === 'review' || project.status === 'reviewReady'
      ? 'reviewer'
      : 'compiler';

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveHandler('Review Comments');
  }, [project.resolved, saveHandler]);

  const undoPredicate = useCallback(
    (comment: Comment): boolean => {
      const isAuthoredByUser = comment.author === user;
      const isResolveComment = comment.metaData?.commentType === 'resolve';

      if (!isAuthoredByUser || !isResolveComment) {
        return false;
      }

      const isCompilationPhase =
        project.status === 'compilation' &&
        !project.resolved?.reviewer?.date &&
        comment.role === 'compiler';

      const isReviewPhase =
        project.status === 'review' && comment.role === 'reviewer';

      return isCompilationPhase || isReviewPhase;
    },
    [project.status, project.resolved?.reviewer?.date, user]
  );

  return (
    <ProjectCard highlighted={shouldHighlightBorder}>
      <StepRevision
        projectStatus={project.status}
        isWorkStepsResolved={isWorkStepsResolved}
      />
      <div>
        <CommentsHeaderContainer
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <CommentsHeaderTitle level={4}>
            {`REVIEWER'S COMMENTS`}
          </CommentsHeaderTitle>
          <ResolveAction
            project={project}
            user={user}
            isProjectResolved={isProjectResolved}
            isWorkStepsResolved={isWorkStepsResolved}
            viewOnly={viewOnly || project.status === 'done'}
            shouldRenderNewCommentFlow={shouldRenderNewCommentFlow}
          />
        </CommentsHeaderContainer>
        {shouldRenderNewCommentFlow ? (
          <ReviewCommentList
            identifier={project.id}
            userRole={userRole}
            user={user}
            readOnly={viewOnly || project.status === 'done'}
            comments={comments}
            isWorkStepsResolved={isWorkStepsResolved}
            onSave={saveReviewComment}
          />
        ) : (
          <Comments
            identifier={project.id}
            userRole={userRole}
            user={user}
            readOnly={viewOnly || project.status === 'done'}
            comments={oldCommentList}
            onSave={saveReviewComment}
            undo={{
              predicate: undoPredicate,
              onUndo: () => {
                dispatch(
                  projectSlice.actions.resolveComments({
                    resolved: undefined,
                  })
                );
              },
            }}
          />
        )}
      </div>
    </ProjectCard>
  );
};
